import "./CustomiseCampaign.scss";
import { useContext, useEffect, useRef, useState, } from "react";
import DebugLogger from "../../helpers/DebugLogger";
import { IconJsxer } from "../../helpers/IconHelper";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { CampaignContext } from "../../contexts/CampaignContext";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import { AuthContext } from "../../contexts/AuthContext";
import { getCampaignCustomisationVarsRoute, getCampaignOverviewRoute } from "../../api/routes";
import NodeList, { NODE_LIST_CONTROLLER, NODE_LIST_DRAG_MODES } from "../../components/NodeList/NodeList";
import { CUSTOMISED_URL_MAP, getValFromObject } from "../../config/FeatureDashboardVarMap";
import { getFeatureAssetBaseUrl } from "../../helpers/FeatureAssets";
import CustomisationControls from "../../components/CustomisationControls/CustomisationControls";
import { DEFAULT_RIGHT_CLICK_MENU, RIGHT_CLICK_MENU_CONTROLLER } from "../../components/RightClickMenu/RightClickMenu";
import { FEATURE_CONTROLS_TYPES } from "../../helpers/FeatureControlsHelper";
import { addCustomFieldButton, BLANK_STRUCTURES, CUSTOMISATION_MODULES, ENUM_LISTS, exportFeatureConfigJsonFileDataFromNodeListData, getInitialNewFormatData, processNodesToFinalDataObject, RenderNodeName, updateNodeListData } from "../../helpers/devBuildTools/TimelineBuilder";
import CustomisationWorkspaceMain from "../../components/v2/CustomisationWorkspace/CustomisationWorkspaceMain";
import { AutoDiscoverNodeLinks } from "../../helpers/devBuildTools/AutoDiscoveryFunctions";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";

const CUSTOMISATION_NODE_LEVELS = {
    component: 'COMPONENT',     // Disruptors, features, effects etc. are all components. This level is draggable / reordable and represents the layer order.
    section: 'SECTION',         // Sections include top level options for the feature, ie. Chat Commands, Customisation, Arcade Data, etc.
    tab: 'TAB',                 // Tabs are the next level down, exactly what the tabs are in the original UI
    group: 'GROUP',             // A group of related customisable items
    family: 'FAMILY',           // A family of related items, ie. a family of variables that can be customised. This is the top level of customisation.
    // In the node list it is only at the item level and below that we actually populate with actual customisation data
    item: 'ITEM',               // Related variables than can be customised. An array of settings, allowing for multiple items (families in the old UI)
    // With allow multiples we would expand our items to list each added item and add more items, 
    // with allow multiples set to false the item isn't expandable, we just click it to open the customisations.

    // Variables are displayed in the customisation UI, but are not nodes in the node list
}

const CustomiseCampaign = () => {
    const { campaignId } = useParams();
    const [campaignContext, setCampaignContext] = useContext(CampaignContext);
    const [authContext, setAuthContext] = useContext(AuthContext);
    const navigate = useNavigate();

    const [showingWorkspacePreview, setShowingWorkspacePreview] = useState(false);

    const campaignData = campaignContext.campaignData;
    console.log("CustomiseCampaign: campaignData", campaignData);

    const [orderedFeatureData, setOrderedFeatureData] = useState(null);
    // Make sure all our features have a stacking order assigned.
    // Not worried about saving this until we make changes because I'll do the same check on the feature page.
    const verifyStacking = (campaignData) => {
        console.log('Verify stacking order (before): ', JSON.parse(JSON.stringify(campaignData.features)));
        let highestStack = 0;
        for (let i = 0; i < campaignData.features.length; i++) {
            if (typeof campaignData.features[i].stackingOrder !== 'undefined' && campaignData.features[i].stackingOrder > highestStack) {
                highestStack = campaignData.features[i].stackingOrder;
            }
        }
        for (let i = 0; i < campaignData.features.length; i++) {
            if (typeof campaignData.features[i].stackingOrder === 'undefined') {
                highestStack++;
                campaignData.features[i].stackingOrder = highestStack;
                console.log('Verify stacking order (set stack) Feature: ', campaignData.features[i].contentLabel, ' - Stacking Order: ', highestStack);
            }
        }
        console.log('Verify stacking order (after): ', JSON.parse(JSON.stringify(campaignData.features)));
    }

    const [forceUpdate, setForceUpdate] = useState(0);
    const forceUpdateNow = () => {
        setForceUpdate((oldVal) => oldVal + 1);
    }

    const [customisationNodes, setCustomisationNodes] = useState([]);
    const customisationNodesRef = useRef(null);
    const buildTopLevelNodes = (featureOrder = null, campaignData = null) => {
        featureOrder = featureOrder || orderedFeatureData;
        campaignData = campaignData || campaignContext.campaignData;
        if (!featureOrder || !campaignData) {
            console.error("Cannot build top level nodes without feature order or campaign data");
        }

        const topLevelNodes = [];
        for (let i = 0; i < featureOrder.length; i++) {
            const feature = featureOrder[i];
            const node = {
                id: feature._id,
                name: feature.contentLabel || feature.varKey,
                path: feature._id,
                hasChildren: true,
                expanded: false,
                enabled: true,
                draggable: true,

                featureData: feature,
                nodeLevel: CUSTOMISATION_NODE_LEVELS.component,

                highlighted: true,

                clickFunction: (node, nodes) => {
                    selectNewFormatFeatureData(node);
                    setShowingCustomisationControls(false);
                    NODE_LIST_CONTROLLER.selectNodeByIdAndPath('original-data-structure-nodes-list', node.id, node.path, false, true, true);
                    return false; // True would indicate that the node should be selected
                },
                
                subNodes: [
                    {
                        id: feature._id + '-arcade-data',
                        name: 'Arcade Data',
                        path: feature._id + '.arcadeData',
                        hasChildren: false,
                        expanded: false,
                        enabled: false,

                        // highlighted: Math.random() < 0.5 ? true : false, // Randomly highlight some items for testing

                        featureData: feature,
                        nodeLevel: CUSTOMISATION_NODE_LEVELS.section,
                    },
                    {
                        id: feature._id + '-chat-commands',
                        name: 'Chat Commands',
                        path: feature._id + '.chatCommands',
                        hasChildren: true,
                        expanded: false,
                        enabled: false,

                        // highlighted: Math.random() < 0.5 ? true : false, // Randomly highlight some items for testing

                        featureData: feature,
                        nodeLevel: CUSTOMISATION_NODE_LEVELS.section,
                    },
                    {
                        id: feature._id + '-themes',
                        name: 'Themes',
                        path: feature._id + '.themes',
                        hasChildren: true,
                        expanded: false,
                        enabled: false,

                        // highlighted: Math.random() < 0.5 ? true : false, // Randomly highlight some items for testing

                        featureData: feature,
                        nodeLevel: CUSTOMISATION_NODE_LEVELS.section,
                    },
                    {
                        id: feature._id + '-customisation',
                        name: 'Customisation',
                        path: feature._id + '.customisation',
                        hasChildren: true,
                        expanded: false,
                        enabled: true,
                        dynamicSubNodes: true,

                        // highlighted: Math.random() < 0.5 ? true : false, // Randomly highlight some items for testing

                        featureData: feature,
                        nodeLevel: CUSTOMISATION_NODE_LEVELS.section,
                        populateSubNodesFunction: getFeatureCustomisationNodes, // This function will be called to get the sub-nodes for this section

                        clickFunction: openRestrictedCustomisation, // Function to call when the customisation section is clicked
                        customisationRestriction: null              // No restriction for the customisation section, we can customise everything
                    },
                ]
            }
            topLevelNodes.push(node);
        }
        console.log("Top level nodes: ", topLevelNodes);
        setCustomisationNodes(topLevelNodes);
    }

    const highlightTopLevelNodesWithNoNewFormatData = (nodesIn = null) => {
        const nodes = nodesIn ?? customisationNodes;
        console.log("Highlighting top level nodes with no new format data: ", nodes);
        if (!nodes || !Array.isArray(nodes)) {
            console.warn("No nodes provided or nodes is not an array");
            return;
        }
        for (let i = 0; i < nodes.length; i++) {
            const featureId = nodes[i].featureData.feature._id;
            // Check if we have new format customisations for this feature
            if (newFormatCustomisationsRef.current && newFormatCustomisationsRef.current[featureId]) {
                // We have new format customisations for this feature, so we don't need to highlight it
                nodes[i].highlighted = false;
            } else {
                // We don't have new format customisations for this feature, so we highlight it
                nodes[i].highlighted = true;
            }
        }
    }

    const getFeatureCustomisationNodes = (node, nodes) => {
        const featureData = node.featureData;
        const featureConfig = featureData.feature;
        const customisationNodes = [];
        if (!featureConfig || !featureConfig.customisation || !featureConfig.customisation.length) {
            console.warn("No customisation data found for feature: ", featureData.contentLabel);
            return customisationNodes;
        }
        console.log("Building customisation nodes for feature: ", featureData.contentLabel, " - Config: ", featureConfig.customisation);
        for (let i = 0; i < featureConfig.customisation.length; i++) {
            const customisation = featureConfig.customisation[i];
            const customisationNode = {
                id: featureData._id + '-' + customisation.tabKey,
                name: customisation.tabTitle || customisation.tabKey,
                path: featureData._id + '.' + customisation.tabKey,
                hasChildren: true,
                // expanded: false,
                enabled: true,

                featureData: featureData,
                nodeLevel: CUSTOMISATION_NODE_LEVELS.tab,

                clickFunction: openRestrictedCustomisation,
                customisationRestriction: {
                    tab: customisation.tabKey,
                    tabTitle: customisation.tabTitle || customisation.tabKey,
                }
            }
            customisationNodes.push(customisationNode);
            // Create a sub-node for each customisation option (group)
            const groups_subNodes = [];
            customisationNode.subNodes = groups_subNodes;
            if (customisation.options && customisation.options.length) {
                for (let j = 0; j < customisation.options.length; j++) {
                    const option = customisation.options[j];
                    const groupNode = {
                        id: featureData._id + '-' + customisation.tabKey + '-' + option.varKey,
                        name: option.groupTitle || option.varKey,
                        path: featureData._id + '.' + customisation.tabKey + '.' + option.varKey,
                        hasChildren: true,
                        enabled: true,

                        featureData: featureData,
                        nodeLevel: CUSTOMISATION_NODE_LEVELS.group,

                        clickFunction: openRestrictedCustomisation,
                        customisationRestriction: {
                            tab: customisation.tabKey,
                            tabTitle: customisation.tabTitle || customisation.tabKey,
                            group: option.varKey || option.groupTitle,
                            groupTitle: option.groupTitle || option.varKey,
                        }
                    }
                    groups_subNodes.push(groupNode);
                    // Create a sub-node for each item in the group
                    const items_subNodes = [];
                    groupNode.subNodes = items_subNodes;
                    if (option.variables && option.variables.length) {
                        for (let k = 0; k < option.variables.length; k++) {
                            const variable = option.variables[k];
                            const itemNode = {
                                id: featureData._id + '-' + customisation.tabKey + '-' + option.varKey + '-' + variable.varName,
                                name: variable.label || variable.varName,
                                path: featureData._id + '.' + customisation.tabKey + '.' + option.varKey + '.' + variable.varName,
                                hasChildren: variable.allowMultiple || false, // If allowMultiples is true, we can expand this node to show multiple items
                                // expanded: false,
                                enabled: true,

                                featureData: featureData,
                                nodeLevel: variable.allowMultiple ? CUSTOMISATION_NODE_LEVELS.family : CUSTOMISATION_NODE_LEVELS.item, // If allowMultiple is true, this is a group, otherwise it's an item
                            }

                            if (variable.allowMultiple) {
                                // Allow us to view the customisation on the family level
                                itemNode.clickFunction = openRestrictedCustomisation;
                                itemNode.customisationRestriction = {
                                    tab: customisation.tabKey,
                                    tabTitle: customisation.tabTitle || customisation.tabKey,
                                    group: option.varKey || option.groupTitle,
                                    groupTitle: option.groupTitle || option.varKey,
                                    family: variable.varName,
                                    familyLabel: variable.label || variable.varName
                                };
                            }

                            // console.log("Checking for unlinked variables for feature: ", featureData, " - selectedNewFormatFeatureId: ", selectedNewFormatFeatureIdRef.current);
                            // On the selected feature we need to hilight any variables that are not currently linked to a new format customisation
                            if (featureData.feature._id === selectedNewFormatFeatureIdRef.current /* && option.varKey === 'triggerSFX' */) {
                                // Get the varSchema for this variable, if it exists
                                const varSchema = variable.valSchema;
                                let allNodesLinked = true; // Assume all nodes are linked until we find one that isn't
                                // console.log("Checking variable schema for: ", variable.varName, " - Schema: ", varSchema);
                                for (let vals in varSchema) {
                                    const valConfig = varSchema[vals];
                                    // console.log("Checking variable value: ", vals, " - Config: ", valConfig);
                                    let valTypeConfig = null;
                                    // Find the matching object in FEATURE_CONTROLS_TYPES
                                    for (let prop in FEATURE_CONTROLS_TYPES) {
                                        if (FEATURE_CONTROLS_TYPES[prop].type.toLowerCase() === valConfig.type.toLowerCase()) {
                                            valTypeConfig = FEATURE_CONTROLS_TYPES[prop];
                                            break;
                                        }
                                    }
                                    if (valTypeConfig && valTypeConfig.isCosmetic !== true) {
                                        // Construct the path link to the legacy data node
                                        const legacyDataNodePath = customisation.tabKey + '.' + option.varKey + '.' + variable.varName + '.' + vals;
                                        // console.log("Checking legacy data node path: ", legacyDataNodePath);
                                        if (!isLegacyNodeLinked(legacyDataNodePath)) {
                                            // If the node is not linked, we need to highlight it
                                            // console.log("Variable value is not linked: ", vals);
                                            itemNode.highlighted = true; // Highlight this item node
                                            allNodesLinked = false; // At least one node is not linked
                                            break;
                                        }
                                    }
                                }
                                if (allNodesLinked) {
                                    itemNode.highlighted = false; // If all nodes are linked, don't highlight
                                }
                            } else {
                                itemNode.highlighted = false;
                            }

                            items_subNodes.push(itemNode);
                            
                            // We need to fetch the items for this variable
                            const customisationVars = customisationVarsRef.current.current[featureData._id];
                            
                            // If allowMultiple is true, we can add a sub-node for each item
                            if (customisationVars && customisationVars[customisation.tabKey] && customisationVars[customisation.tabKey][option.varKey] && customisationVars[customisation.tabKey][option.varKey][variable.varName]) {
                                itemNode.subNodes = [];
                                const items = customisationVars[customisation.tabKey][option.varKey][variable.varName];
                                if (variable.allowMultiple) {
                                    if (items && items.length) {
                                        for (let l = 0; l < items.length; l++) {
                                            const item = items[l];
                                            const itemSubNode = {
                                                id: featureData._id + '-' + customisation.tabKey + '-' + option.varKey + '-' + variable.varName + '-' + l,
                                                name: item.name || item?.filename?.filename || item.varName || 'Item ' + (l + 1),
                                                path: featureData._id + '.' + customisation.tabKey + '.' + option.varKey + '.' + variable.varName + '.' + l,
                                                hasChildren: false, // Items are not expandable, they are just clicked to edit
                                                // expanded: false,
                                                enabled: true,

                                                featureData: featureData,
                                                nodeLevel: CUSTOMISATION_NODE_LEVELS.item,
                                                itemData: item,
                                                variableConfig: variable,

                                                clickFunction: openRestrictedCustomisation,
                                                customisationRestriction: {
                                                    tab: customisation.tabKey,
                                                    tabTitle: customisation.tabTitle || customisation.tabKey,
                                                    group: option.varKey || option.groupTitle,
                                                    groupTitle: option.groupTitle || option.varKey,
                                                    family: variable.varName,
                                                    familyLabel: variable.label || variable.varName,
                                                    item: item
                                                }
                                            }
                                            itemNode.subNodes.push(itemSubNode);
                                        }
                                        itemNode.itemData = items;
                                        // Add a sub-node for adding a new item
                                        const addItemNode = {
                                            id: featureData._id + '-' + customisation.tabKey + '-' + option.varKey + '-' + variable.varName + '-add',
                                            name: '[ Add Item ]',
                                            path: featureData._id + '.' + customisation.tabKey + '.' + option.varKey + '.' + variable.varName + '.add',
                                            hasChildren: false,
                                            // expanded: false,
                                            enabled: true,

                                            featureData: featureData,
                                            nodeLevel: CUSTOMISATION_NODE_LEVELS.item,
                                            itemData: null,
                                        }
                                        itemNode.subNodes.push(addItemNode);
                                    }
                                } else {
                                    if (items && items.length) {
                                        itemNode.itemData = items[0]; // If allowMultiple is false, we only have one item
                                        itemNode.variableConfig = variable;

                                        itemNode.clickFunction = openRestrictedCustomisation;
                                        itemNode.customisationRestriction = {
                                            tab: customisation.tabKey,
                                            tabTitle: customisation.tabTitle || customisation.tabKey,
                                            group: option.varKey,
                                            groupTitle: option.groupTitle || option.varKey,
                                            family: variable.varName,
                                            familyLabel: variable.label || variable.varName,
                                            item: items[0]
                                        }
                                    }
                                }
                            }   
                            
                        }
                    }
                }
            }
        }
        console.log("Customisation nodes for feature: ", featureData.contentLabel, " - Nodes: ", customisationNodes);
        return customisationNodes;
    }

    const buildSubNodes = (node, nodes) => {
        if (!node || !node.populateSubNodesFunction) {
            console.warn("No node or populate function provided for building sub-nodes");
            return [];
        }
        return node.populateSubNodesFunction(node, nodes);
    }

    const [showingCustomisationControls, setShowingCustomisationControls] = useState(false);
    const [customisationNode, setCustomisationNode] = useState(null);
    const [customisationRestriction, setCustomisationRestriction] = useState(null);
    const openRestrictedCustomisation = (node, nodes) => {
        console.log("Open item customisation for node: ", node);
        if (node.nodeLevel !== CUSTOMISATION_NODE_LEVELS.component && node.nodeLevel !== CUSTOMISATION_NODE_LEVELS.section /* node.expanded ||  !node.hasChildren || !node.subNodes || node.subNodes.length === 0*/) {
            setCustomisationRestriction(node.customisationRestriction);
            setCustomisationNode(node);
            setShowingCustomisationControls(true);
            selectNewFormatFeatureData(node);

            return true;
        }
        return false;
    }

    /** Stuff related to new format for customisations */
    const [newFormatCustomisations, setNewFormatCustomisations] = useState({});
    const newFormatCustomisationsRef = useRef(null);
    const [selectedNewFormatFeatureId, setSelectedNewFormatFeatureId] = useState(null);
    const selectedNewFormatFeatureIdRef = useRef(null);
    const [selectedLegacyFeatureData, setSelectedLegacyFeatureData] = useState(null);
    const selectedLegacyFeatureDataRef = useRef(null);
    useEffect(() => {
        // pull out new format data from local storage
        const newFormatData = localStorage.getItem('icast_newFormatCustomisations_raw');
        if (newFormatData) {
            try {
                const parsedData = JSON.parse(newFormatData);
                
                // Bring it up to date with the current node structure
                for (const featureId in parsedData) {
                    updateNodeListData(parsedData[featureId]);
                }
                console.log("New format customisations data loaded from local storage and updated: ", parsedData);

                setNewFormatCustomisations(parsedData);
                newFormatCustomisationsRef.current = parsedData; // Store the data in a ref for quick access
            } catch (error) {
                console.error("Error parsing new format customisations data from local storage: ", error);
                // localStorage.removeItem('newFormatCustomisations'); // Clear invalid data
            }
        } else {
            console.log("No new format customisations data found in local storage");
            // Initialize with an empty object if not found
            setNewFormatCustomisations({});
            newFormatCustomisationsRef.current = {}; // Initialize the ref with an empty object
        }
    }, []);
    useEffect(() => {
        console.log("Customisation nodes updated: ", customisationNodes);
        customisationNodesRef.current = customisationNodes; // Store the nodes in a ref for quick access
        highlightTopLevelNodesWithNoNewFormatData(customisationNodes);
    }, [customisationNodes, newFormatCustomisations]);

    const shallowCopyAndRemoveParentFromSubNodes = (node) => {
        if (node.subNodes) {
            const newSubNodes = [...node.subNodes]; // Create a shallow copy of the subNodes array
            for (let i = 0; i < newSubNodes.length; i++) {
                const subNode = {...newSubNodes[i]};
                subNode.parent = null;
                newSubNodes[i] = subNode;
                shallowCopyAndRemoveParentFromSubNodes(subNode);
            }
            node.subNodes = newSubNodes;
        }
    };

    const [copiedModule, setCopiedModule] = useState(null);
    const copiedModule_ref = useRef(null);
    useEffect(() => {
        copiedModule_ref.current = copiedModule;
    }, [copiedModule]);
    const copyModule = (module) => {
        const shallowModuleCopy = { ...module };
        shallowModuleCopy.parent = null;
        shallowModuleCopy.active = false;
        shallowModuleCopy.selected = false;

        shallowCopyAndRemoveParentFromSubNodes(shallowModuleCopy);

        console.log("Copying module: ", shallowModuleCopy);
        const copiedModuleData = JSON.parse(JSON.stringify(shallowModuleCopy));
        setCopiedModule(copiedModuleData);
        toast.success("Module copied...");
        console.log("Copied module: ", copiedModuleData);
    }
    const pasteModule = (groupNode) => {
        const copiedModuleData = copiedModule || copiedModule_ref.current;
        if (!copiedModuleData) {
            return;
        }
        const moduleDataToPaste = JSON.parse(JSON.stringify(copiedModuleData));
        moduleDataToPaste.parent = groupNode; // Set the parent to the group node we are pasting into
        const idSplit = copiedModuleData.id.split('-');
        idSplit.pop(); // Remove the last part of the ID to get the base ID
        const newId = idSplit.join('-') + '-' + Date.now(); // Create a new ID based on the group node ID and current timestamp
        moduleDataToPaste.id = newId; // Set the new ID
        const pathSplit = copiedModuleData.path.split('-');
        pathSplit.pop(); // Remove the last part of the path to get the base path
        const newPath = pathSplit.join('-') + '-' + Date.now(); // Create a new path based on the group node path and current timestamp
        moduleDataToPaste.path = newPath; // Set the new path
        console.log("Pasting module: ", moduleDataToPaste, " into group node: ", groupNode);
        // Add the module to the group node's subNodes
        if (!groupNode.subNodes) {
            groupNode.subNodes = [];
        }
        groupNode.subNodes.push(moduleDataToPaste);
        verifyNewFormatNodePositions(groupNode.subNodes);
        toast.success("Module pasted into group: " + groupNode.name);
    }

    const [copiedGroup, setCopiedGroup] = useState(null);
    const copiedGroup_ref = useRef(null);
    useEffect(() => {
        copiedGroup_ref.current = copiedGroup;
    }, [copiedGroup]);
    const copyGroup = (groupNode) => {
        const shallowGroupCopy = { ...groupNode };
        shallowGroupCopy.parent = null;
        shallowGroupCopy.active = false;
        shallowGroupCopy.selected = false;

        shallowCopyAndRemoveParentFromSubNodes(shallowGroupCopy);

        console.log("Copying group: ", shallowGroupCopy);
        const copiedGroupData = JSON.parse(JSON.stringify(shallowGroupCopy));
        setCopiedGroup(copiedGroupData);
        toast.success("Group copied...");
        console.log("Copied group: ", copiedGroupData);
    }
    const pasteGroup = (nodeToPasteInto) => {
        const copiedGroupData = copiedGroup || copiedGroup_ref.current;
        if (!copiedGroupData) {
            return;
        }
        const groupDataToPaste = JSON.parse(JSON.stringify(copiedGroupData));
        groupDataToPaste.parent = nodeToPasteInto; // Set the parent to the node we are pasting into
        const idSplit = copiedGroupData.id.split('-');
        idSplit.pop(); // Remove the last part of the ID to get the base ID
        const newId = idSplit.join('-') + '-' + Date.now(); // Create a new ID based on the node ID and current timestamp
        groupDataToPaste.id = newId; // Set the new ID
        const pathSplit = copiedGroupData.path.split('-');
        pathSplit.pop(); // Remove the last part of the path to get the base path
        const newPath = pathSplit.join('-') + '-' + Date.now(); // Create a new path based on the node path and current timestamp
        groupDataToPaste.path = newPath; // Set the new path
        console.log("Pasting group: ", groupDataToPaste, " into node: ", nodeToPasteInto);
        // Add the group to the node's subNodes
        if (!nodeToPasteInto.subNodes) {
            nodeToPasteInto.subNodes = [];
        }
        nodeToPasteInto.subNodes.push(groupDataToPaste);
        verifyNewFormatNodePositions(nodeToPasteInto.subNodes);
        toast.success("Group pasted into: " + nodeToPasteInto.name);
    }

    const showSaveOptionsMenu = (e) => {
        const rightClickMenu = [];
        rightClickMenu.push({
            label: "Save to File",
            action: (e) => {
                saveLocalStorageDataToFile();
                RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
            }
        });
        rightClickMenu.push({
            label: "Import from File",
            action: (e) => {
                loadLocalStorageDataFromFile();
                RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
            }
        });
        RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, rightClickMenu);
    }

    const saveToLocalStorage = (e) => {
        console.log("Saving new format customisations to local storage: ", newFormatCustomisationsRef.current);
        // We need to strip out some data before we save! 
        // Recursively copy all subnode arrays and subnodes and remove references to parent nodes
        const strippedData = getStrippedNewFormatData();
        
        // Save the stripped data to local storage
        localStorage.setItem('icast_newFormatCustomisations_raw', JSON.stringify(strippedData));
        toast.success("New format customisations saved to local storage");
    }

    const saveLocalStorageDataToFile = () => {
        const strippedData = getStrippedNewFormatData();
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(strippedData, null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        const fileName = 'icast_newFormatCustomisations_' + (campaignData ? campaignData.campaignName.replace(/\s+/g, '_').toLowerCase() : 'campaign') + '_' + new Date().toISOString().split('T')[0] + '.json';
        downloadAnchorNode.setAttribute("download", fileName);
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
        toast.success("New format customisations saved to file: " + fileName);
    }

    const loadLocalStorageDataFromFile = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json,application/json';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) {
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const fileData = e.target.result;
                    const parsedData = JSON.parse(fileData);
                    // Bring it up to date with the current node structure
                    for (const featureId in parsedData) {
                        updateNodeListData(parsedData[featureId]);
                    }
                    console.log("New format customisations data loaded from file and updated: ", parsedData);

                    setNewFormatCustomisations(parsedData);
                    newFormatCustomisationsRef.current = parsedData; // Store the data in a ref for quick access
                    // Save the data to local storage (not needed - we need to click the button to do that!)
                    // localStorage.setItem('icast_newFormatCustomisations_raw', JSON.stringify(parsedData));
                    toast.success("New format customisations loaded from file: " + file.name);
                }
                catch (error) {
                    console.error("Error reading new format customisations data from file: ", error);
                    toast.error("Error reading new format customisations data from file: " + error.message);
                }
            };
            reader.readAsText(file);
        };
        fileInput.click();
    }

    const getStrippedNewFormatData = () => {
        const strippedData = {};
        for (const featureId in newFormatCustomisationsRef.current) {
            if (newFormatCustomisationsRef.current.hasOwnProperty(featureId)) {
                const featureData = newFormatCustomisationsRef.current[featureId];

                console.log('Flattened new format customisations for feature: ', featureId, ' - Data: ', processNodesToFinalDataObject(featureData));

                strippedData[featureId] = featureData.map(node => {
                    const strippedNode = { ...node };
                    // Remove parent reference
                    strippedNode.parent = null; // Remove parent reference
                    delete strippedNode.parent; // Remove featureData reference
                    const stripSubNodes = (parentNode) => {
                        if (parentNode.subNodes && Array.isArray(parentNode.subNodes)) {
                            parentNode.subNodes = parentNode.subNodes.map(subNode => {
                                const strippedSubNode = { ...subNode };
                                strippedSubNode.parent = null; // Remove parent reference
                                delete strippedSubNode.parent; // Remove featureData reference
                                if (strippedSubNode.autoDiscover) {
                                    // If the node has autoDiscover, we need to remove the autoDiscover data as this causes some bloating
                                    // it will be added back in when we reload the data
                                    delete strippedSubNode.autoDiscover;
                                }
                                stripSubNodes(strippedSubNode); // Recursively strip sub-nodes
                                return strippedSubNode;
                            });
                        }
                    }
                    stripSubNodes(strippedNode); // Strip sub-nodes recursively
                    return strippedNode; // Return the stripped node
                });
            }
        }
        console.log("Stripped new format customisations data: ", strippedData);
        return strippedData;
    }

    const getProcessedNewFormatData = () => {
        // This function processes the new format data and returns it in a format that can be used
        const processedData = {};
        for (const featureId in newFormatCustomisationsRef.current) {
            if (newFormatCustomisationsRef.current.hasOwnProperty(featureId)) {
                const featureData = newFormatCustomisationsRef.current[featureId];
                const processedFeatureData = processNodesToFinalDataObject(featureData);
                console.log('Processed new format customisations for feature: ', featureId, ' - Data: ', processedFeatureData);
                processedData[processedFeatureData.featureKey || featureId] = processedFeatureData;
            }
        }
        console.log("Processed new format customisations data: ", processedData);
        return processedData;
    }

    const selectNewFormatFeatureData = (node) => {
        const selectedFeatureId = node.featureData.feature._id;
        setSelectedNewFormatFeatureId(selectedFeatureId);
        selectedNewFormatFeatureIdRef.current = selectedFeatureId; // Update the ref to the selected feature ID
        setSelectedLegacyFeatureData(node.featureData);
        selectedLegacyFeatureDataRef.current = node.featureData; // Update the ref to the selected legacy feature data
        if (typeof newFormatCustomisationsRef.current[selectedFeatureId] === 'undefined') {
            // If we don't have new format customisations for this feature, initialize it
            const featureName = node.featureData.feature.featureName;
            const instanceId = node.featureData._id;
            const instanceName = node.featureData.contentLabel || node.featureData.varKey || "Unnamed Feature";
            const newFormatData = [getInitialNewFormatData(selectedFeatureId, featureName, instanceId, instanceName)];
            setNewFormatCustomisations((oldData) => {
                const allNewFormatData = { ...oldData, [selectedFeatureId]: newFormatData };
                newFormatCustomisationsRef.current = allNewFormatData; // Update the ref to the new data
                console.log("Initializing new format customisations for feature: ", selectedFeatureId, " - Data: ", allNewFormatData);
                return allNewFormatData;
            });
        }
        if (newFormatCustomisationsRef.current?.[selectedFeatureId] && newFormatCustomisationsRef.current?.[selectedFeatureId]?.[0]?.featureKey !== node.featureData.feature.varKey) {
            console.log('varKey set for: ', newFormatCustomisationsRef.current[selectedFeatureId]);
            newFormatCustomisationsRef.current[selectedFeatureId][0].featureKey = node.featureData.feature.varKey;
        }
    }

    const resetNewFormatFeatureData = (id, name) => {
        console.log("Reset new format feature data for id: ", id);
        setNewFormatCustomisations((oldData) => {
            const newData = { ...oldData };
            const instanceId = selectedLegacyFeatureDataRef.current._id;
            const instanceName = selectedLegacyFeatureDataRef.current.contentLabel || selectedLegacyFeatureDataRef.current.varKey || "Unnamed Feature";
            newData[id] = [getInitialNewFormatData(id, name, instanceId, instanceName)];
            newFormatCustomisationsRef.current = newData; // Update the ref to the new data
            return newData;
        });
    }

    const verifyNewFormatNodePositions = (nodesList) => {
        // newFormatCustomisationData is a node list.
        // Any nodes in this list or subNodes that have alwaysAtBottom set to true should be moved to the end of the list.
        // The original node data is going to be modified here as we are not using dynamic sub-nodes for the new format.
        console.log("Verifying new format node positions: ", nodesList);
        if (!nodesList || !Array.isArray(nodesList)) {
            console.warn("No new format customisation data provided or data is not an array");
            return;
        }
        // const nodesToMove = [];
        for (let i = 0; i < nodesList.length; i++) {
            const node = nodesList[i];
            if (addNodeToNewFormatData.alwaysAtTop && i > 0) {
                console.log("Node with alwaysAtTop found: ", node);
                // nodesToMove.push(node);
                nodesList.splice(i, 1); // Remove the node from its current position
                i--; // Adjust the index since we removed an item
                nodesList.unshift(node); // Add it to the start of the list
            }
            if (node.alwaysAtBottom && i < nodesList.length - 1) {
                console.log("Node with alwaysAtBottom found: ", node);
                // nodesToMove.push(node);
                nodesList.splice(i, 1); // Remove the node from its current position
                nodesList.push(node); // Add it to the end of the list
                // If we only have other alwaysAtBottom nodes after this one, then break out early as we don't need to move them!
                if (nodesList.slice(i + 1).every(n => n.alwaysAtBottom)) {
                    console.log("All remaining nodes are alwaysAtBottom, breaking out of loop");
                    break;
                }
                i--; // Adjust the index since we removed an item
            }
            if (node.subNodes && node.subNodes.length) {
                verifyNewFormatNodePositions(node.subNodes); // Recursively check sub-nodes
            }
        }
    }

    const nodeDraggedAndDropped = (node, newIndex, subNodesList, nodes) => {
        verifyNewFormatNodePositions(nodes);
        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
    }

    const addNodeToNewFormatData = (featureId, nodes, nodeStructure, nodeNamePrefix) => {
        console.log("Add node for feature: ", featureId, " - Nodes: ", nodes, " - Structure: ", nodeStructure);
        if (!featureId || !nodes || !nodeStructure) {
            console.error("Invalid parameters for addNode: ", featureId, nodes, nodeStructure);
            return;
        }
        const newNode = JSON.parse(JSON.stringify(nodeStructure)); // {...nodeStructure};
        const isNestedGroup = newNode.customisationContainerType === 'group' && nodes[0]?.parent && nodes[0].parent.customisationContainerType === 'group';
        if (isNestedGroup) {
            // Go through sub nodes and sub nodes of sub nodes removing nodes set to removeFromNestedGroups: true
            const removeFromNestedGroups = (subNodes) => {
                for (let i = subNodes.length - 1; i >= 0; i--) {
                    const subNode = subNodes[i];
                    if (subNode.removeFromNestedGroups) {
                        console.log("Removing node from nested groups: ", subNode);
                        subNodes.splice(i, 1); // Remove the node from the array
                    } else if (subNode.subNodes && subNode.subNodes.length) {
                        removeFromNestedGroups(subNode.subNodes); // Recursively check sub-nodes
                    }
                }
            }
            removeFromNestedGroups(newNode.subNodes);
            nodeNamePrefix = nodeNamePrefix.replace('Customisation', 'Nested');
        }

        // If this node is a group, add a group-config to it's subNodes (at the top)
        if (newNode.customisationContainerType === 'group') {
            const groupConfigNode = JSON.parse(JSON.stringify(BLANK_STRUCTURES.groupConfig));
            newNode.subNodes.unshift(groupConfigNode);
        }
        // If this node is a module, add a module-config to it's subNodes (at the top)
        if (newNode.customisationContainerType === 'module') {
            const moduleConfigNode = JSON.parse(JSON.stringify(BLANK_STRUCTURES.moduleConfig));
            newNode.subNodes.unshift(moduleConfigNode);
        }

        newNode.id += '-' + Date.now(); // Ensure unique ID for the new node
        newNode.path += '-' + Date.now(); // Ensure unique path for the new node
        // push the new node into the second to last position in the nodes array
        nodes.splice(nodes.length - 1, 0, newNode);
        verifyNewFormatNodePositions(nodes); // Verify the positions of the nodes after adding the new one
        newNode.name = !newNode.renamable ? nodeNamePrefix : nodeNamePrefix + ' ' + (nodes.indexOf(newNode) + ((nodes[0] && nodes[0].alwaysAtTop) ? 0 : 1)); // Name it based on its position in the array
        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
        return newNode; // Return the new node for further processing if needed
    }

    const addTimelineStep = (featureId, timelineNodes) => {
        console.log("Add timeline step for nodes: ", featureId, timelineNodes);
        addNodeToNewFormatData(featureId, timelineNodes, BLANK_STRUCTURES.step, 'Step');
    }

    const addTimelineAudioGroup = (featureId, audioGroupNodes) => {
        console.log("Add timeline audio group for nodes: ", featureId, audioGroupNodes);
        addNodeToNewFormatData(featureId, audioGroupNodes, BLANK_STRUCTURES.audioGroup, 'Audio Group');
    }

    const addTimelineAudio = (featureId, audioNodes) => {
        console.log("Add timeline audio for nodes: ", featureId, audioNodes);
        addNodeToNewFormatData(featureId, audioNodes, BLANK_STRUCTURES.timelineAudio, 'Audio');
    }

    const addDisruptorGroup = (featureId, disruptorNodes) => {
        console.log("Add disruptor group for nodes: ", featureId, disruptorNodes);
        addNodeToNewFormatData(featureId, disruptorNodes, BLANK_STRUCTURES.disruptorGroup, 'Disruptor Group');
    }

    const addTimelineDisruptor = (featureId, disruptorNodes) => {
        console.log("Add disruptor for nodes: ", featureId, disruptorNodes);
        addNodeToNewFormatData(featureId, disruptorNodes, BLANK_STRUCTURES.timelineDisruptor, 'Disruptor');
    }

    const addCustomisationGroup = (featureId, customisationNodes) => {
        console.log("Add customisation component for nodes: ", featureId, customisationNodes);
        return addNodeToNewFormatData(featureId, customisationNodes, BLANK_STRUCTURES.customisationGroup, 'Customisation Group');
    }

    const addCustomisationModule = (featureId, customisationNodes, moduleDataStructure) => {
        console.log("Add customisation module for nodes: ", featureId, customisationNodes, moduleDataStructure);
        const newNode = addNodeToNewFormatData(featureId, customisationNodes, moduleDataStructure, moduleDataStructure.name);
        const addFieldButton = {...addCustomFieldButton};
        addFieldButton.id = newNode.id + '-add-custom-field';
        newNode.subNodes.push(addFieldButton);
        return newNode;
    }

    const addProgressBar = (featureId, customisationNodes) => {
        const progressBarGroup = addCustomisationGroup(featureId, customisationNodes);
        progressBarGroup.name = 'Progress Bar';
        // Add the modules we need for a progress bar
        addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.position);
        addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.dimensions);
        addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.appearance);
        addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.border);
        const bg = addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.fill);
        bg.name = 'Background';
        const fg = addCustomisationModule(featureId, progressBarGroup.subNodes, CUSTOMISATION_MODULES.fill);
        fg.name = 'Fill';
        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
    }

    const addCustomField = (featureId, customisationNodes, typeKey, fieldTypeData) => {
        console.log("Add custom field for nodes: ", featureId, customisationNodes, typeKey, fieldTypeData);
        const newCustomFieldNode = addNodeToNewFormatData(featureId, customisationNodes, BLANK_STRUCTURES.customCustomisationField, fieldTypeData.label || fieldTypeData.type);
        newCustomFieldNode.type = typeKey; // Set the type of the custom field
        newCustomFieldNode.subNodes[1].name = fieldTypeData.label || fieldTypeData.type; // Set the name of the custom field to the label or type
    }

    const changeCustomisationFieldType = (node, fieldTypeData) => {
        console.log("Change customisation field type for node: ", node, " - New type: ", fieldTypeData);
        if (!node || !fieldTypeData) {
            console.error("Invalid parameters for changeCustomisationFieldType: ", node, fieldTypeData);
            return;
        }
        // Update the node's name based on the new type
        node.name = fieldTypeData.label || fieldTypeData.type; // Update the name of the custom field to the label or type
        // Update the parent node's type
        if (node.parent) {
            node.parent.type = fieldTypeData.type; // Update the type of the parent node to the new type
        }
    }

    const clearNodeValue = (node) => {
        console.log("Clearing node value for: ", node);
        if (node && node.valueLink) {
            node.name = '<No Value>';
            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
            toast.success("Node value cleared");
        }
    }

    const clearNodeLinkage = (node) => {
        console.log("Clearing node linkage for: ", node);
        if (node && node.link) {
            node.link = null; // Clear the link
            node.name = "<Not Linked>"; // Update the name to indicate it's not linked
            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
            toast.success("Node linkage cleared");
        }
    }

    const showLinkedNode = (node) => {
        console.log("Show linked node: ", node);
        if (node && node.link) {
            const nodeLinkSplit = node.link.split('.');
            nodeLinkSplit.pop(); // Remove the last part of the link to get the parent node
            const nodeList = newFormatCustomisationsRef.current[selectedNewFormatFeatureId];
            if (nodeList && nodeList.length) {
                const linkId = nodeList[0].instanceId + '-' + nodeLinkSplit.join('-');
                const linkPath = nodeList[0].instanceId + '.' + nodeLinkSplit.join('.');
                NODE_LIST_CONTROLLER.selectNodeByIdAndPath("original-data-structure-nodes-list", linkId, linkPath);
            }
        }
    }

    // Check if any of our linkable nodes are linked to a legacy data node path, (node.link === legacyDataNodePath)
    // return true if they are, false otherwise.
    const isLegacyNodeLinked = (legacyDataNodePath) => {
        // console.log("Checking if legacy node is linked: ", legacyDataNodePath);
        if (!legacyDataNodePath || !selectedNewFormatFeatureIdRef.current) {
            // console.warn("No legacy data node path or selected feature ID provided");
            return false;
        }
        const newFormatData = newFormatCustomisationsRef.current[selectedNewFormatFeatureIdRef.current];
        if (!newFormatData || !Array.isArray(newFormatData)) {
            // console.warn("No new format customisations data found for feature ID: ", selectedNewFormatFeatureIdRef.current);
            return false;
        }
        for (const node of newFormatData) {
            // console.log("Checking node: ", node, node.link, " against legacy data node path: ", legacyDataNodePath);
            if (node.link === legacyDataNodePath) {
                // console.log("Legacy node is linked: ", node);
                return true; // Found a linked node
            }
            // Check sub-nodes recursively
            if (node.subNodes && node.subNodes.length) {
                const found = checkSubNodesForLink(node.subNodes, legacyDataNodePath);
                if (found) {
                    return true;
                }
            }
        }
        // console.log("No linked nodes found for legacy data path: ", legacyDataNodePath);
        return false; // No linked nodes found
    }

    const checkSubNodesForLink = (subNodes, legacyDataNodePath) => {
        for (const subNode of subNodes) {
            if (subNode.link === legacyDataNodePath) {
                // console.log("Legacy node is linked in sub-nodes: ", subNode);
                return true; // Found a linked node in sub-nodes
            }
            // Check sub-nodes recursively
            if (subNode.subNodes && subNode.subNodes.length) {
                const found = checkSubNodesForLink(subNode.subNodes, legacyDataNodePath);
                if (found) {
                    return true; // Found a linked node in deeper sub-nodes
                }
            }
        }
        // console.log("No linked nodes found in sub-nodes for legacy data path: ", legacyDataNodePath);
        return false; // No linked nodes found in sub-nodes
    }

    const deleteNode = (node) => {
        console.log("Delete node: ", node);
        if (node && node.parent && node.parent.subNodes) {
            const index = node.parent.subNodes.indexOf(node);
            if (index > -1) {
                node.parent.subNodes.splice(index, 1); // Remove the node from its parent's subNodes
                NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                toast.success("Node deleted successfully");
            } else {
                console.warn("Node not found in parent's subNodes: ", node);
            }
        } else {
            console.error("Invalid node or parent structure for deletion: ", node);
        }
    }

    const [rightClickedNode, setRightClickedNode] = useState(null);
    const newFormatListRightClicked = (e, node, nodes, onDone) => {
        console.log("New format node right clicked: ", node);
        setRightClickedNode(node);
        const rightClickMenu = [];
        if (node.depth === 0) {
            rightClickMenu.push({
                label: "Reset",
                action: (e) => {
                    console.log("Reset clicked for new format node: ", node);
                    resetNewFormatFeatureData(node.id, node.name);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
            rightClickMenu.push({
                label: "Export JSON File",
                action: (e) => {
                    console.log("Export JSON File clicked for new format node: ", node);
                    const featureData = newFormatCustomisationsRef.current[node.id];
                    if (featureData) {
                        exportFeatureConfigJsonFileDataFromNodeListData(featureData);
                    }
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.renamable) {
            rightClickMenu.push({
                label: "Change",
                action: (e) => {
                    console.log("Rename clicked for new format node: ", node);
                    NODE_LIST_CONTROLLER.renameNode("new-format-nodes-list", node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.defaultName) {
            rightClickMenu.push({
                label: "Reset to Default",
                action: (e) => {
                    console.log("Reset Name clicked for new format node: ", node);
                    node.name = node.defaultName; // Reset the name to the default
                    NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.linkable && node.link) {
            rightClickMenu.push({
                label: "View Linked Node",
                action: (e) => {
                    console.log("View Linked Node clicked for new format node: ", node);
                    toast.info("Linked to: " + node.link);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    showLinkedNode(node);
                }
            }); 
            rightClickMenu.push({
                label: "Clear Link",
                action: (e) => {
                    console.log("Clear Link clicked for new format node: ", node);
                    clearNodeLinkage(node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.valueLink) {
            rightClickMenu.push({
                label: "Clear Value",
                action: (e) => {
                    console.log("Clear Value clicked for new format node: ", node);
                    clearNodeValue(node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.removable) {
            rightClickMenu.push({
                label: "Delete",
                action: (e) => {
                    console.log("Delete clicked for new format node: ", node);
                    deleteNode(node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.customisationContainerType === 'module') {
            rightClickMenu.push({
                label: "Copy Module",
                action: (e) => {
                    console.log("Copy Module clicked for new format node: ", node);
                    copyModule(node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.customisationContainerType === 'group') {
            if (copiedModule || copiedModule_ref.current) {
                rightClickMenu.push({
                    label: "Paste Module",
                    action: (e) => {
                        console.log("Paste Module clicked for new format node: ", node);
                        pasteModule(node);
                        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    }
                });
            }
        }
        if (node.customisationContainerType === 'group') {
            rightClickMenu.push({
                label: "Copy Group",
                action: (e) => {
                    console.log("Copy Group clicked for new format node: ", node);
                    copyGroup(node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if ((node.customisationContainerType === 'group' || node.id === 'customisation') && (copiedGroup || copiedGroup_ref.current)) {
            rightClickMenu.push({
                label: "Paste Group",
                action: (e) => {
                    console.log("Paste Group clicked for new format node: ", node);
                    pasteGroup(node);
                    NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.customisationContainerType === 'group' || node.customisationContainerType === 'module') {
            rightClickMenu.push({
                label: "Auto Discover Links",
                action: (e) => {
                    console.log("Auto Discover Links clicked for new format node: ", node);
                    const linksAdded = AutoDiscoverNodeLinks(customisationNode, customisationRestriction, node);
                    if (linksAdded) {
                        toast.success("Links discovered and added!");
                    } else {
                        toast.error("No links found to add.");
                    }
                    NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    forceUpdateNow();
                }
            });
        }
        let stepsNode = null;
        if (node.stepChoice) {
            if (rightClickMenu.length) {
                rightClickMenu.push({ label: '-- Choose Step' }); // Add a divider before the step choice options
            }
            console.log("Node has step choice, building right click menu for steps");
            stepsNode = NODE_LIST_CONTROLLER.selectNodeByIdAndPath("new-format-nodes-list", node.dynamicName.id, node.dynamicName.path, false, false, false);
            if (stepsNode) {
                NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                for (let i = 0; i < stepsNode.subNodes.length - 1; i++) {
                    const option = {
                        label: stepsNode.subNodes[i].name,
                        action: (e) => {
                            console.log("Step choice clicked for new format node: ", node, " - Option: ", stepsNode.subNodes[i]);
                            // Set the step choice to the selected option
                            node.dynamicLinkedIndex = i;
                            stepsNode.selected = false;
                            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                        }
                    }
                    rightClickMenu.push(option);
                }
            }
        }
        if (node.isBoolean) {
            if (rightClickMenu.length) {
                rightClickMenu.push({ label: '-- Toggle Value' }); // Add a divider before the toggle option
            }
            console.log("Node is boolean, adding toggle option to right click menu");
            rightClickMenu.push({
                label: node.booleanValue ? "Set to False" : "Set to True",
                action: (e) => {
                    console.log("Toggle boolean value for new format node: ", node);
                    node.booleanValue = !node.booleanValue; // Toggle the boolean value
                    node.name = node.booleanValue ? 'True' : 'False'; // Update the name based on the boolean value
                    NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        if (node.isEnum) {
            if (rightClickMenu.length) {
                rightClickMenu.push({ label: '-- Choose Value' }); // Add a divider before the enum options
            }
            console.log("Node is enum, adding options to right click menu");
            // Add options for each enum value
            const enumValues = (Array.isArray(node.enumValues) ? node.enumValues : ENUM_LISTS?.[node.enumValues]) ?? [];
            for (const enumValue of enumValues) {
                const option = {
                    label: enumValue,
                    action: (e) => {
                        console.log("Enum value selected for new format node: ", node, " - Value: ", enumValue);
                        node.name = node.enumValue = enumValue; // Set the selected enum value
                        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    }
                };
                rightClickMenu.push(option);
            }
        }
        if (node.clickAction) {
            switch (node.clickAction) {
                case "add-customisation-module":
                    console.log("Add customisation module clicked for node: ", node);
                    // Step through our CUSTOMISATION_MODULES and add them to the right click menu, for each one we use the name property as the label
                    for (const moduleKey in CUSTOMISATION_MODULES) {
                        if (CUSTOMISATION_MODULES.hasOwnProperty(moduleKey)) {
                            const moduleData = CUSTOMISATION_MODULES[moduleKey];
                            const moduleRightClickMenuNode = {
                                label: moduleData.name,
                                action: (e) => {
                                    console.log("Add customisation module clicked for node: ", node, " - Module: ", moduleData);
                                    addCustomisationModule(selectedNewFormatFeatureId, node.parent.subNodes, moduleData);
                                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                                }
                            };
                            rightClickMenu.push(moduleRightClickMenuNode);
                        }
                    }
                    rightClickMenu.push({
                        label: "Add Progress Bar",
                        action: (e) => {
                            console.log("Add Progress Bar clicked for node: ", node);
                            addProgressBar(selectedNewFormatFeatureId, node.parent.subNodes);
                            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                        }
                    });
                    break;
                case "add-customisation-custom-field":
                case "change-customisation-field-type":
                    console.log(`${node.clickAction === 'add-customisation-custom-field' ? 'Add' : 'Change'} customisation custom field clicked for node: `, node);
                    // Step through our FEATURE_CONTROLS_TYPES and add them to the right click menu, for each one we use the type property as the label
                    // We only need to add ones that have newFormatCustomisationField: true
                    for (const fieldTypeKey in FEATURE_CONTROLS_TYPES) {
                        if (FEATURE_CONTROLS_TYPES.hasOwnProperty(fieldTypeKey) && FEATURE_CONTROLS_TYPES[fieldTypeKey].newFormatCustomisationField) {
                            const fieldTypeData = FEATURE_CONTROLS_TYPES[fieldTypeKey];
                            const fieldRightClickMenuNode = {
                                label: fieldTypeData.label || fieldTypeData.type,
                                action: (e) => {
                                    console.log(`${node.clickAction === 'add-customisation-custom-field' ? 'Add' : 'Change'} customisation custom field clicked for node: `, node, " - Field Type: ", fieldTypeData);
                                    node.clickAction === 'add-customisation-custom-field' ? 
                                        addCustomField(selectedNewFormatFeatureId, node.parent.subNodes, fieldTypeKey, fieldTypeData)
                                        : changeCustomisationFieldType(node, fieldTypeData);
                                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                                }
                            };
                            rightClickMenu.push(fieldRightClickMenuNode);
                        }
                    }
                    break;


                default:
                    console.warn("Unhandled click action for new format node: ", node.clickAction);
                    break;
            }
        }

        if (rightClickMenu.length) {
            RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, rightClickMenu, () => { if (stepsNode) {stepsNode.selected = false}; NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list"); onDone(); });
            return true; // We handled the right click
        } else {
            console.warn("No right click menu items available for new format node: ", node);
            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
            return false; // We did not handle the right click
        }
    }

    const [selectedNewFormatNode, setSelectedNewFormatNode] = useState(null);
    const [selectedNewFormatNodeList, setSelectedNewFormatNodeList] = useState(null);
    const newFormatNodeClicked = (e, node, nodes) => {
        console.log("New format node clicked: ", node);
        if (node.clickAction) {
            switch (node.clickAction) {
                case "add-timeline-step":
                    console.log("Add timeline step clicked for node: ", node);
                    addTimelineStep(nodes[0].id, node.parent.subNodes);
                    break;
                case "add-timeline-audio-group":
                    console.log("Add timeline audio group clicked for node: ", node);
                    addTimelineAudioGroup(nodes[0].id, node.parent.subNodes);
                    break;
                case "add-timeline-audio":
                    console.log("Add timeline audio clicked for node: ", node);
                    addTimelineAudio(nodes[0].id, node.parent.subNodes);
                    break;
                case "add-timeline-disruptor-group":
                    console.log("Add timeline disruptor group clicked for node: ", node);
                    addDisruptorGroup(nodes[0].id, node.parent.subNodes);
                    break;
                case "add-timeline-disruptor":
                    console.log("Add timeline disruptor clicked for node: ", node);
                    addTimelineDisruptor(nodes[0].id, node.parent.subNodes);
                    break;
                case "add-customisation-group":
                    console.log("Add customisation group clicked for node: ", node);
                    addCustomisationGroup(nodes[0].id, node.parent.subNodes);
                    break;
                default:
                    console.warn("Unhandled click action for new format node: ", node.clickAction);
                    break;
            }
        }
        if (node.linkable) {
            console.log("Node is linkable, selecting node: ", node);
            setSelectedNewFormatNode(node);
            setSelectedNewFormatNodeList(nodes);
            toast.info("Right click on a legacy data node to link to this new format node");
            return true; // We handled the click, select the node
        } else
        if (node.valueLink) {
            console.log("Node has value link, selecting node: ", node);
            setSelectedNewFormatNode(node);
            setSelectedNewFormatNodeList(nodes);
            toast.info("Right click on a legacy data node and copy a value to set the value of to this new format node");
            return true; // We handled the click, select the node
        } else
        if (node.defaultToRightClickMenu) {
            console.log("Default to right-click menu: ", node);
            // Force the right click menu to show the step choices
            node.active = newFormatListRightClicked(e, node, nodes, () => {
                node.active = false; // Deselect the node after showing the menu
                NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
            });
            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
        }
        return false;   // Usually return false so we don't select the node
    }

    const legacyCustomisationVarRightClicked = (e, dataNode, varVal, valSchema, varIndex, schemaKey) => {
        e.preventDefault(); 
        console.log("Right clicked on var: ", dataNode, varVal, valSchema, varIndex, schemaKey);
        const rightClickMenu = [
            { 
                label: dataNode,
                action: (e) => {
                    console.log("Copy Node Path clicked for var: ", dataNode);
                    navigator.clipboard.writeText(dataNode);
                    toast.success("Data node path copied to clipboard");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            },
            {
                label: (selectedNewFormatNode && selectedNewFormatNodeList) ? 
                    "Link To " + selectedNewFormatNodeList[0].name + " " + selectedNewFormatNode.parent.name :
                    "Select Node to Link To First!",
                action: (selectedNewFormatNode && selectedNewFormatNodeList) ?
                    (e) => {
                        selectedNewFormatNode.name = "<" + dataNode + ">";
                        selectedNewFormatNode.link = dataNode;
                        selectedNewFormatNode.selected = false;
                        setSelectedNewFormatNode(null);
                        setSelectedNewFormatNodeList(null);
                        NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                        toast.success("Data node linked");
                        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    } : null
            }
        ];
        if (selectedNewFormatNode && selectedNewFormatNodeList) {
            rightClickMenu.push({
                label: "Link Group (multiples link) To " + selectedNewFormatNodeList[0].name + " " + selectedNewFormatNode.parent.name,
                action: (e) => {
                    const groupNodeArray = dataNode.split('.');
                    groupNodeArray.pop(); // Remove the last part of the dataNode path
                    const groupDataNode = groupNodeArray.join('.'); // Join the remaining parts to get the
                    selectedNewFormatNode.name = "<" + groupDataNode + ">";
                    selectedNewFormatNode.link = groupDataNode;
                    selectedNewFormatNode.selected = false;
                    setSelectedNewFormatNode(null);
                    setSelectedNewFormatNodeList(null);
                    NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                    toast.success("Group node linked");
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                }
            });
        }
        let prop = dataNode.split('.').pop(); // Get the last part of the dataNode path

            if (valSchema[prop] && valSchema[prop].valOptions && valSchema[prop].valOptions.length) {
                rightClickMenu.push({ label: '-- Copy/Set Value' }); // Add a divider before the
                // options
                for (let i = 0; i < valSchema[prop].valOptions.length; i++) {
                    const option = {
                        label: valSchema[prop].valOptions[i].label + ': ' + (valSchema[prop].valOptions[i].value ?? valSchema[prop].valOptions[i].val),
                        action: (e) => {
                            console.log("Copy Value clicked for var: ", dataNode, " - Option Data: ", valSchema[prop].valOptions[i]);
                            navigator.clipboard.writeText((valSchema[prop].valOptions[i].value ?? valSchema[prop].valOptions[i].val));
                            if (selectedNewFormatNode && selectedNewFormatNode.valueLink) {
                                selectedNewFormatNode.name = `${valSchema[prop].valOptions[i].value ?? valSchema[prop].valOptions[i].val}`;
                                selectedNewFormatNode.selected = false;
                                setSelectedNewFormatNode(null);
                                setSelectedNewFormatNodeList(null);
                                NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                            }
                            toast.success("Value copied to clipboard: " + (valSchema[prop].valOptions[i].value ?? valSchema[prop].valOptions[i].val));
                            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                        }
                    };
                    rightClickMenu.push(option);
                }
            } else
            if (valSchema[prop] && valSchema[prop].type?.toLowerCase() === 'toggle') {
                rightClickMenu.push({ label: '-- Copy/Set Value' }); // Add a divider before the
                rightClickMenu.push({
                    label: 'True',
                    action: (e) => {
                        console.log("Copy True clicked for var: ", dataNode);
                        navigator.clipboard.writeText('true');
                        if (selectedNewFormatNode && selectedNewFormatNode.valueLink) {
                            selectedNewFormatNode.name = 'True';
                            selectedNewFormatNode.selected = false;
                            setSelectedNewFormatNode(null);
                            setSelectedNewFormatNodeList(null);
                            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                        }
                        toast.success("Value copied to clipboard: true");
                        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    }
                });
                rightClickMenu.push({
                    label: 'False',
                    action: (e) => {
                        console.log("Copy False clicked for var: ", dataNode);
                        navigator.clipboard.writeText('false');
                        if (selectedNewFormatNode && selectedNewFormatNode.valueLink) {
                            selectedNewFormatNode.name = 'False';
                            selectedNewFormatNode.selected = false;
                            setSelectedNewFormatNode(null);
                            setSelectedNewFormatNodeList(null);
                            NODE_LIST_CONTROLLER.refreshNodeList("new-format-nodes-list");
                        }
                        toast.success("Value copied to clipboard: false");
                        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    }
                });
            }


        RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, rightClickMenu, null);
    }

    /** Make sure we have campiagn data - as this is it's own route we can't guarantee we do! */
    const pullInCampaignData = (pullInCustomisation = true, callback = null) => {
        if (campaignId) {
            axios
                .get(getCampaignOverviewRoute, { params: { campaignID: campaignId }, withCredentials: true })
                .then((response) => {
                    console.log('Got campaign data: ', response.data.campaignData);
                    /* if (pullInCustomisation) {
                        buildTabs(response.data.campaignData);
                        determineIfThemeable(response.data.campaignData);
                    } */
                    verifyStacking(response.data.campaignData);

                    const orderedFeatures = [...response.data.campaignData.features];
                    orderedFeatures.sort((a, b) => b.stackingOrder - a.stackingOrder);
                    setOrderedFeatureData(orderedFeatures);
                    console.log("Ordered feature data: ", orderedFeatures);

                    buildTopLevelNodes(orderedFeatures, response.data.campaignData);

                    setCampaignContext((oldValues) => {
                        return { ...oldValues, initialising: false, campaignData: response.data.campaignData }
                    })

                    getCustomisationVars(response.data.campaignData);
                    /* if (pullInCustomisation) {
                        getCustomisationVars(response.data.campaignData);
                    }
                    if (callback) {
                        callback(true, response);
                    }
                    if (response.data.campaignData.features.length) {
                        let needQuizData = false;
                        for (let i = 0; i < response.data.campaignData.features.length; i++) {
                            // If we are deiting a quiz we need to have quiz data available
                            if (response.data.campaignData.features[i]._id === featureId && response.data.campaignData.features[i].varKey === 'quizPoll') {
                                needQuizData = true;
                                break;
                            }
                        }
                        if (needQuizData) {
                            getCampaignQuizzes();
                            getGenericQuizzes();
                        }
                    } */
                })
                .catch((error) => {
                    toast.error("Error fetching campaign data");
                    if (!callback) {
                        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS);
                    }
                });
        } else {
            if (!callback) {
                navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS);
            }
        }
    }

    const findFeatureData = (campaignData, featureId) => {
        // console.log('Finding feature data from campaign data: ', campaignData);
        if (campaignData && campaignData.features) {
            for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i]._id === featureId) {
                    return campaignData.features[i];
                }
            }
        }
        return null;
    }

    const fixSrcUrls = (featureData, featureId, customisationDataIn = null, dataToFix = null) => {
        const customisationData = customisationDataIn || featureData.feature.customisation;
        // console.log('FIX URL', featureData, customisationData, dataToFix);
        for (let i = 0; i < customisationData.length; i++) {
            // console.log('i loop: ', i, customisationData[i], customisationVarsRef.current, featureId);
            const rootVal = dataToFix ?? customisationVarsRef.current.current[featureId];
            const tabKey = customisationData[i].tabKey;
            // console.log('rootVal, tabKey: ', rootVal, tabKey);
            // console.log('-----');
            for (let j = 0; j < customisationData[i].options.length; j++) {
                // console.log('j loop: ', j);
                const varKey = customisationData[i].options[j].varKey;
                // console.log('varKey: ', varKey);
                // console.log('-----');
                for (let k = 0; k < customisationData[i].options[j].variables.length; k++) {
                    // console.log('k loop: ', k);
                    const varName = customisationData[i].options[j].variables[k].varName;
                    // console.log('varName: ', varName);
                    // console.log('-----');
                    for (let prop in customisationData[i].options[j].variables[k].valSchema) {
                        // console.log('prop loop: ', prop);
                        const type = customisationData[i].options[j].variables[k].valSchema[prop].type.toLowerCase();
                        // console.log('type: ', type);
                        // console.log('-----');
                        if (CUSTOMISED_URL_MAP.hasOwnProperty(type)) {
                            // console.log('We need to generate a Url here!');
                            // We found one we need to map to a url!
                            const vals = getValFromObject(rootVal[tabKey], varKey, varName);
                            // console.log('vals: ', vals);
                            // console.log('-----');
                            if (vals && vals.length) {
                                for (let l = 0; l < vals.length; l++) {
                                    // console.log('vals loop: ', l);

                                    // Reformat legacy string based filenames to be objects...
                                    // SrcUrl has to be part of the object, meaning all feature customisations are going to break...
                                    let filename = vals[l][prop];
                                    if (typeof filename === 'string' || typeof filename === 'undefined' || filename === null) {
                                        filename = { filename: vals[l][prop] };
                                        vals[l][prop] = filename;
                                        filename.isDefault = vals[l].isDefault;
                                    }

                                    // console.log('Filename: ', filename);
                                    if (filename.srcUrl && typeof filename.srcUrl === 'string' && filename.srcUrl.startsWith('blob:')) {
                                        // Do nothing...
                                    } else {
                                        const srcUrl = (typeof filename.filename === 'undefined' || filename.filename === null || filename.filename === '') ? null :

                                            (filename.isDefault ? getFeatureAssetBaseUrl(true) + featureData.feature.defaultAssetFolder : getFeatureAssetBaseUrl(false)
                                                + 'campaigns/' + (campaignId || campaignContext.campaignData._id)
                                                + '/' + (featureId /* + (streamId && vals[l][prop].isStreamLevel ? ('/' + streamId) : '') */))
                                            + '/' + CUSTOMISED_URL_MAP[type].dir
                                            + '/' + (filename.isDefault ? filename.filename : filename.filename.replace(/(?:\.(?![^.]+$)|[^\w.])+/g, ""));

                                        // console.log('srcUrl: ', srcUrl);
                                        filename.srcUrl = srcUrl;
                                    }
                                    filename.originalFile = filename.originalFile || filename.filename;
                                    filename.type = CUSTOMISED_URL_MAP[type].dir;
                                    // console.log( filename.srcUrl);
                                }
                            }

                            // console.log('Urls generated for: ', vals);
                        }
                    }
                }
            }
        }
    }

    const [customisationVars, setCustomisationVars] = useState({});
    const customisationVarsRef = useRef(null);
    const getCustomisationVars = (campaignData, updateThisIdOnly = null) => {
        console.log('Get Campaign / Stream Customisation Vars');
        axios
            .get(/* streamId ? getStreamCustomisationVarsRoute :  */getCampaignCustomisationVarsRoute, {
                params: { campaignID: campaignId, streamID: null/*streamId*/ },
                withCredentials: true
            })
            .then(function (response) {
                // console.log('CAMPAIGN CUSTOMISATION VARS',response.data);
                if (response.data.customisationVars) {
                    if (updateThisIdOnly) {
                        // console.log('Got customisation vars to update Id: ', updateThisIdOnly, response.data.customisationVars);
                        customisationVarsRef.current.current[updateThisIdOnly] = response.data.customisationVars.current[updateThisIdOnly];
                        customisationVarsRef.current.default[updateThisIdOnly] = response.data.customisationVars.default[updateThisIdOnly];
                    } else {
                        customisationVarsRef.current = response.data.customisationVars;

                        if (updateThisIdOnly) {
                            // convert any filenames into urls and remember the original filenames as well while we are at it (so we can delete old files afterwards)
                            // console.log('Generate urls ------>>> ', campaignData)
                            const featureData = findFeatureData(campaignData, updateThisIdOnly);
                            // console.log('Feature data: ', featureData);
                            // const customisationData = featureData.feature.customisation;
                            // console.log('Customisation data: ', customisationData);
                            // console.log('-----');

                            fixSrcUrls(featureData, updateThisIdOnly);
                        } else {
                            // Step through all the features and fix the srcUrls for each one
                            console.log('Generate urls for all features');
                            for (let i = 0; i < campaignData.features.length; i++) {
                                const featureData = findFeatureData(campaignData, campaignData.features[i]._id);
                                // console.log('Feature data: ', featureData);
                                fixSrcUrls(featureData, featureData._id);
                            }
                        }

                        setCustomisationVars(customisationVarsRef.current);
                        console.log('Got customisation vars: ', customisationVarsRef.current);
                    }
                }
            })
            .catch(function (error) {
                toast.error('Error - ' + error);
                // console.log(error.response)
            });
    }

    useEffect(() => {
        setCampaignContext((oldValues) => {
            return { ...oldValues, initialising: true }
        });
        pullInCampaignData();
        /* while (prevFeatures.current[prevFeatures.currentlength - 1] === featureId) {
            prevFeatures.current.pop();
        }
        setShowCleaningUp(false);
        setShowUploadProgress(false);
        setShowingAddTheme(false);
        setShowingEditTheme(false);
        setShowingDeletingTheme(false);
        setShowCommonFontPopup(false); */
    }, [campaignId]);

    return (
        <div className="content-page-container customise-campaign-holder">
            {campaignData && !showingWorkspacePreview &&
            <>
                <div className="content-page-top">
                    <div className="fl-column grow">
                        <div className="fl-row grow">
                            <div className="grow">
                                <h1>Customisation Data Structure</h1>
                            </div>
                            <div className="fl-column shrink">
                                <p>Data stored in local storage will be auto loaded.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content-page-content-container">
                    <div className="content-page-main-content">
                        <div className="fl-row grow">
                            <div className="fl-column">
                                <h5>Campaign Features / Disruptors</h5>
                                <div className="campaign-layers-list-holder">
                                    <NodeList
                                        id="original-data-structure-nodes-list"
                                        nodes={customisationNodes}
                                        onNodeClick={(e, node, nodes) => {
                                            console.log("Node clicked: ", node);
                                            // highlightTopLevelNodesWithNoNewFormatData();
                                            if (node.clickFunction) {
                                                return node.clickFunction(node, nodes);
                                            }
                                            /* if (node.nodeLevel === CUSTOMISATION_NODE_LEVELS.component) {
                                                // Navigate to the feature customisation page
                                                // navigate(`/campaigns/${campaignId}/customise/${node.id}`);
                                                // console.warn("Component level");
                                            } else if (node.nodeLevel === CUSTOMISATION_NODE_LEVELS.section) {
                                                // Navigate to the section customisation page
                                                // navigate(`/campaigns/${campaignId}/customise/${node.id}`);
                                                // console.warn("Section level");
                                            } else {
                                                console.warn("Node level not handled: ", node.nodeLevel);
                                            } */
                                           return false;
                                        }}
                                        onNodeRightClick={
                                            (e, node, nodes, onDone) => {
                                                if (node.depth > 0) {
                                                    return false; // Only show right click menu for top level nodes
                                                }
                                                RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, 
                                                    [{
                                                        label: "Rename",
                                                        action: (e) => {
                                                            console.log("Rename clicked for node: ", node);
                                                            NODE_LIST_CONTROLLER.renameNode("original-data-structure-nodes-list", node);
                                                            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                                                        }
                                                    }], 
                                                    () => {onDone();}
                                                )
                                                return true;
                                            }
                                        }
                                        enableRightClickMenu={true}
                                        getSubNodes={(node, nodes) => {
                                            console.log("Get sub-nodes for node: ", node);
                                            return buildSubNodes(node, nodes);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="fl-column grow fl-start">
                                <h5>Legacy Customisation (hidden options will appear faded with red backgrounds)</h5>
                                {!showingCustomisationControls &&
                                    <p>No customisable item selected. Please select one from the list on the left.</p>
                                }
                                {showingCustomisationControls &&
                                    <>
                                        <div className="fl-row">
                                            <p className="bold-text">{customisationNode.featureData.contentLabel}</p>
                                            {customisationRestriction &&
                                                <>
                                                    {customisationRestriction.tab &&
                                                        <p className="light-text">
                                                            ¬ Tab: {customisationRestriction.tabTitle} {'<'}{customisationRestriction.tab}{'>'}
                                                        </p>
                                                    }
                                                    {customisationRestriction.group &&
                                                        <p className="light-text">
                                                            ¬ Group: {customisationRestriction.groupTitle} {'<'}{customisationRestriction.group}{'>'}
                                                        </p>
                                                    }
                                                    {customisationRestriction.family &&
                                                        <p className="light-text">
                                                            ¬ Family: {customisationRestriction.familyLabel} {'<'}{customisationRestriction.family}{'>'}
                                                        </p>
                                                    }
                                                    {customisationRestriction.item &&
                                                        <p className="light-text">
                                                            ¬ Item: {customisationRestriction.item.name || customisationRestriction.item.varName || customisationRestriction.item.filename?.filename || '<Item>'}
                                                        </p>
                                                    }
                                                </>
                                            }
                                        </div>
                                        {customisationRestriction &&
                                            <div className="campaign-layers-customisations-holder">
                                                <CustomisationControls
                                                    restrictedCustomisation = {customisationRestriction}
                                                    onlyFadeHiddenControls={true}
                                                    singleColumn={true}
                                                    maxFamilyWidth={"350px"}
                                                    tabId={customisationRestriction.tab}
                                                    controlsData={customisationNode.featureData.feature.customisation}
                                                    customisationVars={customisationVars}
                                                    featureId={customisationNode.featureData._id}
                                                    featureData={customisationNode.featureData}
                                                    campaignData={campaignData}
                                                    advancedMode={/* props.advancedMode ||  */true}
                                                    addDisruptor={() => {}}
                                                    showTooltipFunc={() => {}}

                                                    onRightClickOnVar={legacyCustomisationVarRightClicked}
                                                    shouldFlagControl={(dataNodePath) => {return !isLegacyNodeLinked(dataNodePath);}}
                                                />
                                            </div>
                                        }
                                    </>
                                }
                            </div>
                            { authContext.userData.userLevel >= ACCOUNT_TYPES.twitchSuperUser.level &&
                                <div className="fl-column fl-start">
                                    <div className="fl-row">
                                        <h5>New Format</h5>
                                        <div className="grow"></div>
                                        <div className="shrink">
                                            <div className="standard-button save-new-format-data-button" onClick={saveToLocalStorage} onContextMenu={showSaveOptionsMenu}>Save Local</div>
                                        </div>
                                        <div className="shrink">
                                            <div className="standard-button save-new-format-data-button" onClick={() => setShowingWorkspacePreview(true)}>Preview</div>
                                        </div>
                                    </div>
                                    <div className="campaign-layers-list-holder">
                                        {selectedNewFormatFeatureId && newFormatCustomisations[selectedNewFormatFeatureId] &&
                                            <NodeList
                                                id="new-format-nodes-list"
                                                nodes={newFormatCustomisations[selectedNewFormatFeatureId]}
                                                
                                                enableRightClickMenu={true}
                                                onNodeRightClick={(e, node, nodes, onDone) => {
                                                    return newFormatListRightClicked(e, node, nodes, onDone);
                                                }}

                                                onNodeClick={(e, node, nodes) => {
                                                    console.log("New format node clicked: ", node);
                                                    return newFormatNodeClicked(e, node, nodes);
                                                }}

                                                onNodeDragEnd={nodeDraggedAndDropped}

                                                renderNodeName={RenderNodeName}

                                                highlightParentNodeOnHover={true}
                                            />
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </>
            }
            {showingWorkspacePreview &&
                <div className="workspace-preview-holder">
                    <div className="workspace-preview">
                        <CustomisationWorkspaceMain
                            campaignId={campaignId}
                            campaignData={campaignData}
                            featureId={selectedNewFormatFeatureId}
                            customisationVars={customisationVarsRef.current}
                            previewCustomisationConfig={getProcessedNewFormatData()}
                        />
                        <div className="close-preview-button" onClick={() => setShowingWorkspacePreview(false)}>
                            X
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default CustomiseCampaign;
